import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase, handleSupabaseError, OperationType } from '../supabase';
import { checkAndAwardMissions } from '../services/missionService';
import type { CheckinCounterMap } from '../services/types';
import { perfEnd, perfStart } from '../utils/perf';

/**
 * Compat layer: o resto do código consome `user.uid`, `user.email`, etc.
 * (vindo da era Firebase). Aqui exponho um shape equivalente derivado
 * do `auth.users` do Supabase pra não precisar refatorar todos os consumers.
 */
export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  phoneNumber: string | null;
}

function toAuthUser(u: SupabaseUser | null | undefined): AuthUser | null {
  if (!u) return null;
  return {
    uid: u.id,
    email: u.email ?? null,
    displayName:
      (u.user_metadata?.full_name as string | undefined) ??
      (u.user_metadata?.name as string | undefined) ??
      null,
    phoneNumber: u.phone ?? null,
  };
}

interface UserProfile {
  uid: string;
  name?: string;
  phone?: string;
  cpf?: string;
  email?: string;
  event?: string;
  gradeLevel?: string;
  sixpassNumber?: string;
  birthDate?: string;
  isActive?: boolean;
  role: 'student' | 'admin' | 'company';
  gender?: string;
  avatarId?: string;
  avatarFrameId?: string;
  themeId?: string;
  points?: number;
  completedMissions?: string[];
  checkinCount?: number;
  visitedCategories?: string[];
  partnerCheckinCounts?: CheckinCounterMap;
  categoryCheckinCounts?: CheckinCounterMap;
  offerViewsCount?: number;
  partnerId?: string;
  fcmTokens?: string[];
  fcmTokenLastUpdate?: string;
  tutorialSeenAt?: string | null;
}

/**
 * Row do Postgres. Dynamic by design — mappers abaixo validam cada campo.
 * Mesma estratégia do dataService.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DbRow = Record<string, any>;

/** Mapeia row do Postgres (snake_case) pro UserProfile (camelCase). */
function dbRowToProfile(row: DbRow): UserProfile {
  return {
    uid: row.id,
    role: row.role,
    name: row.name ?? undefined,
    email: row.email ?? undefined,
    phone: row.phone ?? undefined,
    cpf: row.cpf ?? undefined,
    birthDate: row.birth_date ?? undefined,
    event: row.event ?? undefined,
    gradeLevel: row.grade_level ?? undefined,
    sixpassNumber: row.sixpass_number ?? undefined,
    isActive: row.is_active ?? undefined,
    gender: row.gender ?? undefined,
    avatarId: row.avatar_id ?? undefined,
    avatarFrameId: row.avatar_frame_id ?? 'default',
    themeId: row.theme_id ?? 'default',
    points: row.points ?? 0,
    completedMissions: row.completed_missions ?? [],
    checkinCount: row.checkin_count ?? 0,
    visitedCategories: row.visited_categories ?? [],
    partnerCheckinCounts: row.partner_checkin_counts ?? {},
    categoryCheckinCounts: row.category_checkin_counts ?? {},
    offerViewsCount: row.offer_views_count ?? 0,
    partnerId: row.partner_id ?? undefined,
    fcmTokens: row.fcm_tokens ?? [],
    fcmTokenLastUpdate: row.fcm_token_last_update ?? undefined,
    tutorialSeenAt: row.tutorial_seen_at ?? null,
  };
}

/** Mapeia UserProfile (camelCase) → row do Postgres (snake_case) pra writes. */
function profileToDbUpdate(data: Partial<UserProfile>): DbRow {
  const out: DbRow = {};
  const map: Record<keyof UserProfile, string> = {
    uid: 'id',
    name: 'name',
    email: 'email',
    phone: 'phone',
    cpf: 'cpf',
    birthDate: 'birth_date',
    event: 'event',
    gradeLevel: 'grade_level',
    sixpassNumber: 'sixpass_number',
    isActive: 'is_active',
    role: 'role',
    gender: 'gender',
    avatarId: 'avatar_id',
    avatarFrameId: 'avatar_frame_id',
    themeId: 'theme_id',
    points: 'points',
    completedMissions: 'completed_missions',
    checkinCount: 'checkin_count',
    visitedCategories: 'visited_categories',
    partnerCheckinCounts: 'partner_checkin_counts',
    categoryCheckinCounts: 'category_checkin_counts',
    offerViewsCount: 'offer_views_count',
    partnerId: 'partner_id',
    fcmTokens: 'fcm_tokens',
    fcmTokenLastUpdate: 'fcm_token_last_update',
    tutorialSeenAt: 'tutorial_seen_at',
  };
  for (const key of Object.keys(data) as (keyof UserProfile)[]) {
    const dbKey = map[key];
    if (dbKey) out[dbKey] = data[key];
  }
  return out;
}

interface AuthContextType {
  user: AuthUser | null;
  profile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInAsLocalAdminForDev: () => Promise<void>;
  signInAsLocalStudentForDev: () => Promise<void>;
  loginStudent: (sixpassNumber: string, password: string) => Promise<void>;
  registerStudent: (
    sixpassNumber: string,
    birthDate: string,
    email: string,
    phone: string,
    password: string,
    gender: string,
    name?: string,
    cpf?: string,
  ) => Promise<void>;
  loginCompany: (email: string, password: string) => Promise<void>;
  createCompanyAccount: (
    email: string,
    password: string,
    partnerId: string,
    partnerName: string,
  ) => Promise<void>;
  resetUserPassword: (email: string) => Promise<void>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  markTutorialSeen: () => Promise<void>;
  resetTutorial: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

const ADMIN_EMAIL_DOMAIN = '@sixformaturas.com.br';

// Usa `??` em vez de `||` pra NÃO sobrescrever valores que o usuário escolheu
// (string vazia, 0 etc são valores válidos — só null/undefined cai no default).
// Bug anterior: o aluno trocava de tema/moldura, mas em algum ciclo de re-load
// o `||` resetava pra 'default' silenciosamente.
const applyGamificationFallbacks = (data: UserProfile): UserProfile => ({
  ...data,
  avatarFrameId: data.avatarFrameId ?? 'default',
  themeId: data.themeId ?? 'default',
  points: data.points ?? 0,
  completedMissions: data.completedMissions ?? [],
  checkinCount: data.checkinCount ?? 0,
  visitedCategories: data.visitedCategories ?? [],
  partnerCheckinCounts: data.partnerCheckinCounts ?? {},
  categoryCheckinCounts: data.categoryCheckinCounts ?? {},
  offerViewsCount: data.offerViewsCount ?? 0,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(
    async (currentUser: SupabaseUser): Promise<UserProfile | null> => {
      perfStart('auth:profile');
      const isEmailAdmin = !!currentUser.email?.toLowerCase().endsWith(ADMIN_EMAIL_DOMAIN);

      try {
        const { data: row, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', currentUser.id)
          .maybeSingle();

        if (error) {
          await handleSupabaseError(error, OperationType.GET, `users/${currentUser.id}`);
          return null;
        }

        if (row) {
          const loaded = dbRowToProfile(row);
          if (isEmailAdmin && loaded.role !== 'admin') {
            await supabase.from('users').update({ role: 'admin' }).eq('id', currentUser.id);
            loaded.role = 'admin';
          }
          perfEnd('auth:profile', 'profile ready');
          return applyGamificationFallbacks(loaded);
        }

        // Sem row em users:
        if (isEmailAdmin) {
          // Admin via Google: cria profile automaticamente.
          const newRow = {
            id: currentUser.id,
            role: 'admin' as const,
            email: currentUser.email ?? null,
            name: (currentUser.user_metadata?.full_name as string | undefined) ?? null,
            points: 0,
            theme_id: 'default',
            avatar_frame_id: 'default',
            completed_missions: [],
            checkin_count: 0,
            visited_categories: [],
            partner_checkin_counts: {},
            category_checkin_counts: {},
            offer_views_count: 0,
          };
          const { error: insertErr } = await supabase.from('users').insert(newRow);
          if (insertErr) {
            await handleSupabaseError(insertErr, OperationType.CREATE, `users/${currentUser.id}`);
            return null;
          }
          perfEnd('auth:profile', 'admin profile created');
          return applyGamificationFallbacks(dbRowToProfile(newRow));
        }

        // Defesa contra race: se o user_metadata indica que é aluno em meio
        // de cadastro (sixpass_number setado pelo signUp), espera 1.5s e
        // tenta de novo antes de assumir que tá bloqueado. O `registerStudent`
        // cria o profile via RPC `claim_student` logo depois do signUp, e
        // esse retry cobre o caso raro de o listener disparar antes da RPC.
        const sixpassFromMetadata = currentUser.user_metadata?.sixpass_number as
          | string
          | undefined;
        if (sixpassFromMetadata) {
          await new Promise((resolve) => setTimeout(resolve, 1500));
          const { data: retryRow } = await supabase
            .from('users')
            .select('*')
            .eq('id', currentUser.id)
            .maybeSingle();
          if (retryRow) {
            perfEnd('auth:profile', 'profile ready (after retry)');
            return applyGamificationFallbacks(dbRowToProfile(retryRow));
          }
          // Mesmo depois do retry, sem profile. Reporta erro específico.
          try {
            window.sessionStorage?.setItem(
              'sixpass:auth-error',
              'Não conseguimos finalizar seu cadastro. Tente novamente em instantes.',
            );
          } catch {
            /* sessionStorage indisponível em modo privado */
          }
          await supabase.auth.signOut();
          perfEnd('auth:profile', 'student profile missing after retry');
          return null;
        }

        // Sem sixpass no metadata = login Google de quem não é admin nem
        // tem profile. Mensagem específica em vez de "faça login com Six Pass"
        // genérico (que confundia company users sem profile configurado).
        const errorMsg = currentUser.email
          ? `Conta "${currentUser.email}" não está configurada no Six Pass. Se você é parceiro, avise a equipe Six. Se é aluno, use seu número Six Pass e senha em "Sou formando".`
          : 'Conta não configurada no Six Pass. Procure a equipe.';
        try {
          window.sessionStorage?.setItem('sixpass:auth-error', errorMsg);
        } catch {
          /* sessionStorage indisponível em modo privado */
        }
        await supabase.auth.signOut();
        perfEnd('auth:profile', 'profile blocked');
        return null;
      } catch (error) {
        await handleSupabaseError(error, OperationType.GET, `users/${currentUser.id}`);
        return null;
      }
    },
    [],
  );

  const handleSession = useCallback(
    async (session: Session | null) => {
      const currentUser = session?.user ?? null;
      if (!currentUser) {
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }
      const loaded = await loadProfile(currentUser);
      // loadProfile pode ter feito signOut (aluno bloqueado): nesse caso
      // o listener já vai disparar com session null e limpar tudo.
      if (!loaded) {
        setLoading(false);
        return;
      }
      setUser(toAuthUser(currentUser));
      setProfile(loaded);
      setLoading(false);
    },
    [loadProfile],
  );

  useEffect(() => {
    perfStart('auth:state');
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      perfEnd('auth:state', 'auth ready');
      void handleSession(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      void handleSession(session);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [handleSession]);

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: {
          // pede sempre conta — útil quando a sessão Google atual não bate
          // com a esperada (admin x aluno).
          prompt: 'select_account',
        },
      },
    });
    if (error) {
      console.error('Error signing in with Google', error);
      throw error;
    }
  }, []);

  const signInAsLocalAdminForDev = useCallback(async () => {
    if (import.meta.env.DEV) {
      setUser(null);
      setProfile({
        uid: 'local-dev-admin',
        role: 'admin',
        email: 'admin.local@sixformaturas.com.br',
        name: 'Admin de Teste',
      });
      return;
    }
    throw new Error('Atalho indisponível em produção.');
  }, []);

  const signInAsLocalStudentForDev = useCallback(async () => {
    if (import.meta.env.DEV) {
      setUser({
        uid: 'local-dev-student',
        email: 'formando.local@sixpass.com',
        displayName: 'Formando de Teste',
        phoneNumber: null,
      });
      setProfile({
        uid: 'local-dev-student',
        role: 'student',
        email: 'formando.local@sixpass.com',
        name: 'Formando de Teste',
        sixpassNumber: '000000',
        gradeLevel: 'Teste',
        event: 'Evento de Teste',
        isActive: true,
        points: 0,
        themeId: 'default',
        avatarFrameId: 'default',
        completedMissions: [],
        checkinCount: 0,
        visitedCategories: [],
        partnerCheckinCounts: {},
        categoryCheckinCounts: {},
        offerViewsCount: 0,
      });
      return;
    }
    throw new Error('Atalho indisponível em produção.');
  }, []);

  const loginStudent = useCallback(async (sixpassNumber: string, password: string) => {
    // RPC SECURITY DEFINER expõe o auth_email vinculado ao sixpass —
    // sem precisar de SELECT direto em students (RLS bloqueia anon).
    const { data: authEmail, error: lookupErr } = await supabase.rpc(
      'lookup_student_auth_email',
      { p_sixpass_number: sixpassNumber },
    );
    if (lookupErr) {
      console.error('[loginStudent] lookup failed', lookupErr);
      throw lookupErr;
    }
    if (!authEmail) {
      throw new Error('Conta não encontrada pra este Six Pass. Faça o cadastro primeiro.');
    }

    const { error } = await supabase.auth.signInWithPassword({
      email: String(authEmail),
      password,
    });
    if (error) {
      console.error('[loginStudent] signIn failed', error);
      throw error;
    }
  }, []);

  const registerStudent = useCallback(
    async (
      sixpassNumber: string,
      birthDate: string,
      email: string,
      phone: string,
      password: string,
      gender: string,
      name?: string,
      cpf?: string,
    ) => {
      // 1. Verifica elegibilidade ANTES de criar conta — RPC usa SECURITY DEFINER
      // pra ler students sem precisar logar.
      const { data: eligibility, error: eligErr } = await supabase.rpc(
        'verify_student_eligibility',
        {
          p_sixpass_number: sixpassNumber,
          p_birth_date: birthDate,
        },
      );
      if (eligErr) throw eligErr;
      switch (eligibility) {
        case 'not_found':
          throw new Error('Número do Six Pass não encontrado.');
        case 'inactive':
          throw new Error('Este Six Pass está inativo.');
        case 'already_claimed':
          throw new Error('Este Six Pass já foi cadastrado. Faça login com sua senha.');
        case 'eligible':
          break;
        default:
          throw new Error('Não foi possível validar seu Six Pass agora.');
      }

      // 2. Cria conta com email/senha. Por padrão Supabase pede confirmação
      // de email, mas a sessão é retornada imediatamente quando o projeto
      // está com Confirm Email desligado OU quando o e-mail já está confirmado.
      const authEmail = email.trim().toLowerCase();
      const { data: signupData, error: signupErr } = await supabase.auth.signUp({
        email: authEmail,
        password,
        options: {
          data: { full_name: name ?? '', sixpass_number: sixpassNumber },
        },
      });
      if (signupErr) {
        console.error('[registerStudent] signUp failed', signupErr);
        throw signupErr;
      }
      const newUser = signupData.user;
      if (!newUser) throw new Error('Falha ao criar usuário.');

      // 3. UMA chamada só: vincula students E cria profile em users.
      //    `claim_student` é SECURITY DEFINER — bypassa RLS e elimina a race
      //    com onAuthStateChange/loadProfile (que poderia disparar signOut
      //    antes do INSERT em users completar).
      const { error: claimErr } = await supabase.rpc('claim_student', {
        p_sixpass_number: sixpassNumber,
        p_birth_date: birthDate,
        p_name: name ?? null,
        p_email: authEmail,
        p_phone: phone ?? null,
        p_cpf: cpf ?? null,
        p_gender: gender ?? null,
      });
      if (claimErr) {
        console.error('[registerStudent] claim_student failed', claimErr);
        throw claimErr;
      }

      // 4. Lê o profile recém-criado (com defaults aplicados pelo DB).
      const { data: profileRow, error: readErr } = await supabase
        .from('users')
        .select('*')
        .eq('id', newUser.id)
        .single();
      if (readErr) {
        console.error('[registerStudent] read users failed', readErr);
        throw readErr;
      }

      // 5. Award de "complete_profile" (mantém compatibilidade com fluxo antigo).
      let extraPoints = 0;
      let extraMissions: string[] = [];
      try {
        const missionResult = await checkAndAwardMissions(newUser.id, 'complete_profile');
        extraPoints = missionResult?.awardedPoints ?? 0;
        extraMissions = missionResult?.completedMissionIds ?? [];
      } catch (missionErr) {
        console.warn('[registerStudent] checkAndAwardMissions falhou (segue):', missionErr);
      }

      const baseProfile = dbRowToProfile(profileRow);
      const finalProfile = applyGamificationFallbacks({
        ...baseProfile,
        points: (baseProfile.points || 0) + extraPoints,
        completedMissions: [...(baseProfile.completedMissions || []), ...extraMissions],
      });

      setUser(toAuthUser(newUser));
      setProfile(finalProfile);
    },
    [],
  );

  const loginCompany = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      console.error('Error logging in company', error);
      throw error;
    }
  }, []);

  const createCompanyAccount = useCallback(
    async (email: string, password: string, partnerId: string, partnerName: string) => {
      // Roda via Edge Function `create-company-account` — usa service_role pra
      // criar o user sem desautenticar o admin que está chamando.
      const { error } = await supabase.functions.invoke('create-company-account', {
        body: { email, password, partnerId, partnerName },
      });
      if (error) {
        console.error('Error creating company account', error);
        throw error;
      }
    },
    [],
  );

  const resetUserPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });
    if (error) {
      console.error('Error sending password reset', error);
      throw error;
    }
  }, []);

  const updateUserProfile = useCallback(
    async (data: Partial<UserProfile>) => {
      if (!user || !profile) return;
      if (profile.uid === 'local-dev-admin' || profile.uid === 'local-dev-student') {
        setProfile((current) =>
          current ? applyGamificationFallbacks({ ...current, ...data }) : current,
        );
        return;
      }
      try {
        const update = profileToDbUpdate(data);
        delete update.id; // id nunca atualizado.
        const { error } = await supabase.from('users').update(update).eq('id', user.uid);
        if (error) throw error;
        setProfile((current) =>
          current ? applyGamificationFallbacks({ ...current, ...data }) : current,
        );
      } catch (error) {
        await handleSupabaseError(error, OperationType.UPDATE, `users/${user.uid}`);
        throw error;
      }
    },
    [profile, user],
  );

  const markTutorialSeen = useCallback(async () => {
    if (!user || !profile) return;
    const seenAt = new Date().toISOString();
    // Atualiza local imediatamente pra não re-abrir o modal.
    setProfile((current) => (current ? { ...current, tutorialSeenAt: seenAt } : current));
    if (profile.uid === 'local-dev-admin' || profile.uid === 'local-dev-student') return;
    try {
      const { error } = await supabase
        .from('users')
        .update({ tutorial_seen_at: seenAt })
        .eq('id', user.uid);
      if (error) throw error;
    } catch (error) {
      console.error('Error marking tutorial seen', error);
      // Silencioso pro usuário — só loga.
    }
  }, [profile, user]);

  const resetTutorial = useCallback(async () => {
    if (!user || !profile) return;
    setProfile((current) => (current ? { ...current, tutorialSeenAt: null } : current));
    if (profile.uid === 'local-dev-admin' || profile.uid === 'local-dev-student') return;
    try {
      const { error } = await supabase
        .from('users')
        .update({ tutorial_seen_at: null })
        .eq('id', user.uid);
      if (error) throw error;
    } catch (error) {
      console.error('Error resetting tutorial', error);
      throw error;
    }
  }, [profile, user]);

  const signOut = useCallback(async () => {
    if (profile?.uid === 'local-dev-admin' || profile?.uid === 'local-dev-student') {
      setUser(null);
      setProfile(null);
      return;
    }
    const { error } = await supabase.auth.signOut();
    if (error) console.error('Error signing out', error);
  }, [profile?.uid]);

  const contextValue = useMemo(
    () => ({
      user,
      profile,
      loading,
      signInWithGoogle,
      signInAsLocalAdminForDev,
      signInAsLocalStudentForDev,
      loginStudent,
      registerStudent,
      loginCompany,
      createCompanyAccount,
      resetUserPassword,
      updateUserProfile,
      markTutorialSeen,
      resetTutorial,
      signOut,
    }),
    [
      user,
      profile,
      loading,
      signInWithGoogle,
      signInAsLocalAdminForDev,
      signInAsLocalStudentForDev,
      loginStudent,
      registerStudent,
      loginCompany,
      createCompanyAccount,
      resetUserPassword,
      updateUserProfile,
      markTutorialSeen,
      resetTutorial,
      signOut,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
